import React from "react";
import css from 'styled-jsx/css';
import PropTypes from 'prop-types';
import { Heading, Link, IconPhone, IconSmartphone, IconPrinter } from "@lmig/lmds-react";
import Image from '@lmig/dotcom-aspect-components/Utility/Image';
import PhoneNumber from '@lmig/dotcom-aspect-components/Utility/PhoneNumber';
import { formatPhoneNumber } from "@lmig/dotcom-aspect-comparion-helpers/phoneNumber";
import AgentImageSocials from "../Agent/AgentImageSocials";
import Rating from '../Rating';
import styles from './OfficeHero.module.scss';

const OfficeHero = ({ office }) => {
  const { className: headingClass, styles: headingStyles } = css.resolve`
    @import './global-styles/tokens';
    .lm-Heading{
      margin: 0;
      font-size: 1.75rem;
    }
    @media screen and (min-width: $lm-breakpoint-sm-min) {
      .lm-Heading{
        font-size: 2.5rem;
      }
    }
  `;

  const { className: iconClass, styles: iconStyles } = css.resolve`
    *{
      margin-right: 0.5rem;
    }
  `;

  let officeImage = office.image;

  if (!office.image?.src) {
    officeImage = {
      src: "/comparion/entity/office/office_placeholder_619x619.jpg",
      alt: `Local office in ${office.address.city}, ${office.address.state.code}`,
    };
  }

  const hasSocial = office?.social?.instagram || office?.social?.linkedin || office?.social?.facebook || office?.social?.twitter;

  return (
    <div className={styles.heroWrapper}>
      <div className={styles.imageSection}>
        {hasSocial ?
          <AgentImageSocials image={officeImage} socials={office.social} imgLgSize='21.875rem' imgSmSize='8.75rem' />
          : <Image imageDomain="IMAGE_EMS" type="circle" preload>
            <img
              itemProp="image"
              className="heroImage"
              src={officeImage.src}
              alt={officeImage.alt}
              width="350"
              height="350"
            />
          </Image>
        }
      </div>
      <div className={styles.textSection}>
        <Heading type="h2-bold" tag="h1" className={headingClass}>
          Comparion Insurance Agency&nbsp;
          <div className={styles.geo} data-testid="officeHero_city">{`${office.address.city}, ${office.address.state.code}`}</div>
        </Heading>
        <div className={styles.quote}>&quot;{office?.quote}&quot;</div>
        <div className={styles.phonesWrapper}>
          {office.phones?.primary?.number && (
            <div className={styles.phone} data-testid="officeHero_phone">
              <IconSmartphone size="24" className={iconClass} />
              <PhoneNumber tel={office.phones.primary.number}>
                <Link caretPosition="none" aria-label="Primary Phone">
                  <span itemProp="telephone">{formatPhoneNumber(office.phones.primary.number)}</span>
                </Link>
              </PhoneNumber>
            </div>
          )}
          {office.phones?.toll?.number && (
            <div className={styles.phone}>
              <IconPhone size="24" className={iconClass} />
              <PhoneNumber tel={office.phones.toll.number}>
                <Link caretPosition="none" aria-label="Toll Free Phone" itemProp="telephone">
                  <span itemProp="telephone">{`${formatPhoneNumber(office.phones.toll.number)}`}</span> (Toll-Free)
                </Link>
              </PhoneNumber>
            </div>
          )}
          {office.phones?.fax?.number && (
            <div className={styles.phone}>
              <IconPrinter size="24" className={iconClass} />
              <PhoneNumber tel={office.phones.fax.number}>
                <Link caretPosition="none" aria-label="Fax Phone" itemProp="telephone">
                  <span itemProp="faxNumber">{`${formatPhoneNumber(office.phones.fax.number)}`}</span> (Fax)
                </Link>
              </PhoneNumber>
            </div>
          )}
        </div>
        <div className={styles.reviewsWrapper}>
          <Rating rating={office.rating} count={office.reviews?.length} isLinked />
        </div>
      </div>
      {iconStyles}
      {headingStyles}
    </div>
  );
};

OfficeHero.propTypes = {
  office: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string.isRequired,
      state: PropTypes.shape({
        code: PropTypes.string.isRequired
      }).isRequired
    }).isRequired
  }).isRequired
};

export default OfficeHero;
